/**
 * Monitoring Integration Layer
 * Integrates all monitoring systems and provides a unified API
 */

import enhancedMonitoring from './enhanced-monitoring.js';
import logger from './structured-logging.js';
import healthCheckSystem from './health-checks.js';
import { initSentry, identifyUser, clearUserIdentification } from './monitoring.js';

class MonitoringIntegration {
  constructor() {
    this.isInitialized = false;
    this.config = {
      enableErrorTracking: true,
      enablePerformanceMonitoring: true,
      enableHealthChecks: true,
      enableStructuredLogging: true,
      enableUserAnalytics: true
    };

    this.init();
  }

  init() {
    try {
      console.log('[MonitoringIntegration] Starting initialization...');

      // NOTE: Sentry initialization is handled by enhanced-monitoring.js
      // Do NOT call initSentry() here to avoid multiple Sentry Session Replay instances

      // Check if enhanced monitoring is initialized (it auto-initializes Sentry)
      try {
        if (enhancedMonitoring && enhancedMonitoring.isInitialized) {
          console.log('[MonitoringIntegration] Enhanced monitoring initialized (includes Sentry)');
        } else {
          console.warn('[MonitoringIntegration] Enhanced monitoring not available');
          // Fallback: Initialize Sentry if enhanced monitoring failed
          if (this.config.enableErrorTracking) {
            try {
              initSentry();
              console.log('[MonitoringIntegration] Sentry initialized successfully (fallback)');
            } catch (sentryError) {
              console.error('[MonitoringIntegration] Sentry initialization failed:', sentryError);
            }
          }
        }
      } catch (enhancedError) {
        console.error('[MonitoringIntegration] Enhanced monitoring failed:', enhancedError);
        // Fallback: Initialize Sentry if enhanced monitoring failed
        if (this.config.enableErrorTracking) {
          try {
            initSentry();
            console.log('[MonitoringIntegration] Sentry initialized successfully (fallback)');
          } catch (sentryError) {
            console.error('[MonitoringIntegration] Sentry initialization failed:', sentryError);
          }
        }
      }

      // Initialize health checks
      if (this.config.enableHealthChecks) {
        try {
          healthCheckSystem.start();
          console.log('[MonitoringIntegration] Health checks started');
        } catch (healthError) {
          console.error('[MonitoringIntegration] Health checks failed:', healthError);
          // Continue without health checks
        }
      }

      // Set up global error handlers
      try {
        this.setupGlobalHandlers();
      } catch (handlerError) {
        console.error('[MonitoringIntegration] Global handlers setup failed:', handlerError);
      }

      // Set up user context tracking
      try {
        this.setupUserContextTracking();
      } catch (trackingError) {
        console.error('[MonitoringIntegration] User context tracking failed:', trackingError);
      }

      this.isInitialized = true;
      console.log('[MonitoringIntegration] Monitoring integration initialized successfully');

      // Mark monitoring as ready in the global ready state
      if (typeof window !== 'undefined' && window.WixellReadyState) {
        window.WixellReadyState.updateState('monitoring', true);
        console.log('[ReadyState] Monitoring systems ready');
      }

    } catch (error) {
      console.error('[MonitoringIntegration] Critical initialization failure:', error);
      // Don't let monitoring failures crash the app
      this.isInitialized = false;

      // Mark monitoring as failed in the global ready state
      if (typeof window !== 'undefined' && window.WixellReadyState) {
        window.WixellReadyState.updateState('monitoring', false, error);
        console.error('[ReadyState] Monitoring initialization failed:', error);
      }
    }
  }

  setupGlobalHandlers() {
    // Enhanced error handling
    window.addEventListener('error', (event) => {
      logger.error('Global error', {
        message: event.message,
        filename: event.filename,
        lineno: event.lineno,
        colno: event.colno,
        stack: event.error?.stack
      });
    });

    window.addEventListener('unhandledrejection', (event) => {
      logger.error('Unhandled promise rejection', {
        reason: event.reason?.message || event.reason,
        stack: event.reason?.stack
      });
    });

    // Performance monitoring
    if (this.config.enablePerformanceMonitoring) {
      this.setupPerformanceObservers();
    }

    // Page visibility tracking
    document.addEventListener('visibilitychange', () => {
      if (document.hidden) {
        logger.info('Page hidden');
        this.trackUserAction('page_hidden');
      } else {
        logger.info('Page visible');
        this.trackUserAction('page_visible');
      }
    });

    // Page unload tracking
    window.addEventListener('beforeunload', () => {
      logger.info('Page unloading');
      this.trackUserAction('page_unload');
    });
  }

  setupPerformanceObservers() {
    // Long task observer
    if ('PerformanceObserver' in window) {
      try {
        const longTaskObserver = new PerformanceObserver((list) => {
          for (const entry of list.getEntries()) {
            logger.warn('Long task detected', {
              duration: entry.duration,
              startTime: entry.startTime,
              name: entry.name
            });
          }
        });
        longTaskObserver.observe({ entryTypes: ['longtask'] });
      } catch (error) {
        logger.warn('Could not observe long tasks', { error: error.message });
      }
    }
  }

  setupUserContextTracking() {
    // Monitor authentication changes
    const originalSetItem = localStorage.setItem;
    localStorage.setItem = function(key, value) {
      const result = originalSetItem.call(this, key, value);

      if (key === 'auth_user') {
        try {
          const user = JSON.parse(value);
          if (user.id) {
            monitoringIntegration.setUserContext(user);
          }
        } catch (e) {
          logger.warn('Failed to parse user data', { key, value });
        }
      }

      return result;
    };

    // Monitor tenant context changes
    const originalSetItemTenant = localStorage.setItem;
    localStorage.setItem = function(key, value) {
      const result = originalSetItemTenant.call(this, key, value);

      if (key === 'tenant_context') {
        try {
          const tenant = JSON.parse(value);
          if (tenant.id) {
            monitoringIntegration.setTenantContext(tenant);
          }
        } catch (e) {
          logger.warn('Failed to parse tenant data', { key, value });
        }
      }

      return result;
    };
  }

  // User context management
  setUserContext(user) {
    if (!user || !user.id) {
      logger.warn('Invalid user context provided');
      return;
    }

    try {
      // Update all monitoring systems
      identifyUser(user.id, user);
      logger.setUserId(user.id);

      logger.info('User context set', { userId: user.id, email: user.email });
      this.trackUserAction('user_context_set', { userId: user.id });
    } catch (error) {
      logger.error('Failed to set user context', { error: error.message, user });
    }
  }

  setTenantContext(tenant) {
    if (!tenant || !tenant.id) {
      logger.warn('Invalid tenant context provided');
      return;
    }

    try {
      // Update all monitoring systems
      logger.setTenantId(tenant.id);

      logger.info('Tenant context set', { tenantId: tenant.id, name: tenant.name });
      this.trackUserAction('tenant_context_set', { tenantId: tenant.id });
    } catch (error) {
      logger.error('Failed to set tenant context', { error: error.message, tenant });
    }
  }

  clearUserContext() {
    try {
      clearUserIdentification();
      logger.clearContext();

      logger.info('User context cleared');
      this.trackUserAction('user_context_cleared');
    } catch (error) {
      logger.error('Failed to clear user context', { error: error.message });
    }
  }

  // User action tracking
  trackUserAction(action, data = {}) {
    try {
      if (logger && typeof logger.userAction === 'function') {
        logger.userAction(action, data);
      }
      if (enhancedMonitoring && typeof enhancedMonitoring.recordEvent === 'function') {
        enhancedMonitoring.recordEvent('user_action', { action, ...data });
      }
    } catch (error) {
      console.error('[MonitoringIntegration] Failed to track user action:', error);
    }
  }

  // API call tracking
  trackAPICall(method, url, data = {}) {
    try {
      if (logger && typeof logger.apiCall === 'function') {
        logger.apiCall(method, url, data);
      }
      if (enhancedMonitoring && typeof enhancedMonitoring.startUserFlow === 'function') {
        enhancedMonitoring.startUserFlow('api_request', `${method}_${url}`);
      }
    } catch (error) {
      console.error('[MonitoringIntegration] Failed to track API call:', error);
    }
  }

  trackAPIResponse(method, url, status, duration, data = {}) {
    try {
      if (logger && typeof logger.apiResponse === 'function') {
        logger.apiResponse(method, url, status, duration, data);
      }
      if (enhancedMonitoring && typeof enhancedMonitoring.completeUserFlowStep === 'function') {
        enhancedMonitoring.completeUserFlowStep('api_request', `${method}_${url}`);
      }

      // Track performance
      if (duration > 5000 && logger && typeof logger.warn === 'function') {
        logger.warn('Slow API response', { method, url, status, duration });
      }
    } catch (error) {
      console.error('[MonitoringIntegration] Failed to track API response:', error);
    }
  }

  // Business event tracking
  trackBusinessEvent(event, data = {}) {
    try {
      if (logger && typeof logger.business === 'function') {
        logger.business(event, data);
      }
      if (enhancedMonitoring && typeof enhancedMonitoring.recordEvent === 'function') {
        enhancedMonitoring.recordEvent('business_event', { event, ...data });
      }
    } catch (error) {
      console.error('[MonitoringIntegration] Failed to track business event:', error);
    }
  }

  // Security event tracking
  trackSecurityEvent(event, data = {}) {
    try {
      if (logger && typeof logger.security === 'function') {
        logger.security(event, data);
      }
      if (enhancedMonitoring && typeof enhancedMonitoring.addAlert === 'function') {
        enhancedMonitoring.addAlert('SECURITY', `Security event: ${event}`, data);
      }
    } catch (error) {
      console.error('[MonitoringIntegration] Failed to track security event:', error);
    }
  }

  // Performance tracking
  trackPerformance(operation, duration, data = {}) {
    try {
      if (logger && typeof logger.performance === 'function') {
        logger.performance(operation, duration, data);
      }
      if (enhancedMonitoring && typeof enhancedMonitoring.recordMetric === 'function') {
        enhancedMonitoring.recordMetric(`performance_${operation}`, duration);
      }
    } catch (error) {
      console.error('[MonitoringIntegration] Failed to track performance:', error);
    }
  }

  // Error tracking
  trackError(error, context = {}) {
    try {
      if (logger && typeof logger.error === 'function') {
        logger.error(error.message || 'Unknown error', {
          stack: error.stack,
          ...context
        });
      }
      if (enhancedMonitoring && typeof enhancedMonitoring.trackError === 'function') {
        enhancedMonitoring.trackError({
          type: 'manual',
          message: error.message,
          stack: error.stack,
          timestamp: new Date().toISOString(),
          ...context
        });
      }
    } catch (e) {
      console.error('[MonitoringIntegration] Failed to track error:', e);
    }
  }

  // Health monitoring
  async getHealthStatus() {
    try {
      const healthReport = healthCheckSystem.getHealthReport();
      const enhancedMetrics = enhancedMonitoring.getMetrics();
      const logStats = logger.getLogStats();

      return {
        health: healthReport,
        metrics: enhancedMetrics,
        logs: logStats,
        timestamp: new Date().toISOString()
      };
    } catch (error) {
      logger.error('Failed to get health status', { error: error.message });
      return {
        health: { status: 'error', message: 'Failed to get health status' },
        metrics: {},
        logs: {},
        timestamp: new Date().toISOString()
      };
    }
  }

  // Configuration
  updateConfig(newConfig) {
    this.config = { ...this.config, ...newConfig };
    logger.info('Monitoring configuration updated', { config: this.config });
  }

  // Export/Import for debugging
  exportDebugData() {
    try {
      return {
        config: this.config,
        health: healthCheckSystem.getHealthReport(),
        metrics: enhancedMonitoring.getMetrics(),
        logs: logger.exportLogs(),
        timestamp: new Date().toISOString()
      };
    } catch (error) {
      logger.error('Failed to export debug data', { error: error.message });
      return null;
    }
  }

  // Public API for components
  getMonitoringAPI() {
    return {
      // User context
      setUserContext: this.setUserContext.bind(this),
      setTenantContext: this.setTenantContext.bind(this),
      clearUserContext: this.clearUserContext.bind(this),

      // Tracking
      trackUserAction: this.trackUserAction.bind(this),
      trackAPICall: this.trackAPICall.bind(this),
      trackAPIResponse: this.trackAPIResponse.bind(this),
      trackBusinessEvent: this.trackBusinessEvent.bind(this),
      trackSecurityEvent: this.trackSecurityEvent.bind(this),
      trackPerformance: this.trackPerformance.bind(this),
      trackError: this.trackError.bind(this),

      // Health and metrics
      getHealthStatus: this.getHealthStatus.bind(this),
      getMetrics: enhancedMonitoring.getMetrics.bind(enhancedMonitoring),

      // Configuration
      updateConfig: this.updateConfig.bind(this),

      // Debug
      exportDebugData: this.exportDebugData.bind(this)
    };
  }
}

// Create and export singleton instance
const monitoringIntegration = new MonitoringIntegration();

export default monitoringIntegration;

// Export convenience methods
export const {
  setUserContext,
  setTenantContext,
  clearUserContext,
  trackUserAction,
  trackAPICall,
  trackAPIResponse,
  trackBusinessEvent,
  trackSecurityEvent,
  trackPerformance,
  trackError,
  getHealthStatus,
  getMetrics,
  updateConfig,
  exportDebugData
} = monitoringIntegration;

// React hook for using monitoring in components
export const useMonitoring = () => {
  return monitoringIntegration.getMonitoringAPI();
};
